import { type ComponentChildren, createContext } from 'preact';
import { useCallback, useContext, useEffect, useId, useRef, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { AlertCircleIcon, AlertTriangleIcon, TrashIcon } from '@xantory/icons';
import { Scrim } from '../Scrim/scrim';
import { Button } from '../Button/button';
import { Input } from '../Input/input';
import { Label } from '../Label/label';
import { useFocusTrap } from '../_hooks/use-focus-trap';
import { useMountTransition } from '../_hooks/use-mount-transition';
import { styles } from './confirm.styles';
import { useT } from '@xantory/i18n';
export interface ConfirmOptions {
  title: string;
  message?: ComponentChildren;
  confirmLabel?: string;
  danger?: boolean;
  /** GitHub-style: require typing this exact string to enable the confirm button. */
  match?: string;
  /** The badge glyph. Defaults to a warning triangle for `danger`, an info mark
   *  otherwise; a delete asks for `trash` so it reads as a delete at a glance. */
  icon?: 'trash' | 'warning' | 'info';
}
type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

/** Promise-based confirm — `const ok = await confirm({ title, ... })`. */
export const useConfirm = (): ConfirmFn => useContext(ConfirmContext);
export function ConfirmProvider({
  children
}: {
  children: ComponentChildren;
}) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState('');
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const last = useRef<ConfirmOptions | null>(null);
  const open = opts !== null;
  const {
    mounted,
    show
  } = useMountTransition(open);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const messageId = useId();
  const matchInputId = useId();
  // aria-modal promises contained focus — actually trap it (+ restore on close).
  useFocusTrap(dialogRef, show);
  const confirm = useCallback<ConfirmFn>(o => {
    last.current = o;
    setOpts(o);
    setTyped('');
    return new Promise<boolean>(resolve => {
      resolver.current = resolve;
    });
  }, []);
  const settle = (result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setOpts(null);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // keep the last options visible during the exit transition
  const view = opts ?? last.current;
  const t = useT('ui');
  const tc = useT('common');
  const matchOk = !view?.match || typed.trim() === view.match;
  return <ConfirmContext.Provider value={confirm}>
      {children}
      {mounted && view && <>
          <Scrim show={show} level="confirm" onClick={() => settle(false)} />
          <div ref={dialogRef} role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={view.message != null ? messageId : undefined} tabIndex={-1} {...stylex.props(styles.dialog, show && styles.show)}>
            <div {...stylex.props(styles.ico, view.danger ? styles.icoDanger : styles.icoBrand)}>
              {view.icon === 'trash' ? <TrashIcon size={22} /> : (view.icon ?? (view.danger ? 'warning' : 'info')) === 'warning' ? <AlertTriangleIcon size={22} /> : <AlertCircleIcon size={22} />}
            </div>
            <h3 id={titleId} {...stylex.props(styles.title)}>
              {view.title}
            </h3>
            {view.message != null && <div id={messageId} {...stylex.props(styles.message)}>
                {view.message}
              </div>}
            {view.match != null && <div {...stylex.props(styles.match)}>
                <Label htmlFor={matchInputId}>{t('typeToConfirm', {
              match: view.match
            })}</Label>
                <Input id={matchInputId} value={typed} autoComplete="off" onInput={e => setTyped((e.currentTarget as HTMLInputElement).value)} />
              </div>}
            <div {...stylex.props(styles.foot)}>
              <Button variant="ghost" onClick={() => settle(false)}>
                {tc('cancel')}
              </Button>
              <Button variant={view.danger ? 'dangerSolid' : 'primary'} disabled={!matchOk} onClick={() => settle(true)}>
                {view.confirmLabel ?? t('confirm')}
              </Button>
            </div>
          </div>
        </>}
    </ConfirmContext.Provider>;
}